import { FC } from "react";
import { useWalletObserver } from "@sundaeswap/wallet-lite";
import type { DepositEntry } from "./AmountStep";
import { DepositStepHeader } from "./DepositStepHeader";

interface ReviewStepProps {
  entries: DepositEntry[];
  error: string | null;
  headOnline: boolean;
  onConfirm: () => void;
  onBack?: () => void;
}

function truncateAddress(addr: string): string {
  if (addr.length <= 20) return addr;
  return `${addr.slice(0, 9)}....${addr.slice(-8)}`;
}

function assetName(assetId: string): string {
  if (assetId === "ada.lovelace") return "ADA";
  const hex = assetId.split(".")[1] ?? "";
  try {
    return Buffer.from(hex, "hex").toString("utf-8") || hex.slice(0, 8);
  } catch {
    return hex.slice(0, 8);
  }
}

export const ReviewStep: FC<ReviewStepProps> = ({
  entries,
  error,
  headOnline,
  onConfirm,
  onBack,
}) => {
  const { mainAddress } = useWalletObserver();
  const address = mainAddress ?? "";

  return (
    <div className="flex flex-col gap-12 items-center w-full">
      {/* Header */}
      <DepositStepHeader
        title="Confirm your deposit"
        description="Your funds will be available after network confirmation."
      />

      {/* Details + CTA + highlight */}
      <div className="flex flex-col gap-3 w-full">
        {/* Details card */}
        <div className="sr-card w-full p-4">
          <div className="flex flex-col gap-2">
            {entries.filter((e) => Number(e.amount) > 0).map((entry, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-sm text-sr-pink-muted">{i === 0 ? "Deposit" : ""}</span>
                <span className="text-sm text-white">
                  {Number(entry.amount).toLocaleString()} {assetName(entry.assetId)}
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between">
              <span className="text-sm text-sr-pink-muted">From</span>
              <span className="text-sm text-sr-pink-muted">
                {truncateAddress(address)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-sr-pink-muted">Network</span>
              <span className="text-sm text-sr-pink-muted">
                Cardano L1 → Gummiworm L2
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-sr-pink-muted">Est. time</span>
              <span className="text-sm text-white">a few min</span>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="flex w-full items-center gap-3">
          {onBack && (
            <button
              className="sr-surface-pill flex h-[54px] w-[54px] shrink-0 items-center justify-center rounded-full text-sr-neutral-300 transition-colors hover:text-white"
              onClick={onBack}
              aria-label="Go back"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          )}
          <button
            className="sr-btn-primary flex-1"
            disabled={!headOnline}
            onClick={onConfirm}
          >
            {headOnline ? "Confirm and deposit" : "Unavailable"}
          </button>
        </div>

        {/* What happens next */}
        <div className="sr-highlight-box w-full">
          <div className="flex flex-col gap-2">
            <p className="text-base font-medium text-sr-pink-light">
              What happens next?
            </p>
            <ol className="text-xs text-sr-pink-bright list-decimal ml-4 flex flex-col gap-1">
              <li>Wait for network confirmation</li>
              <li>Your funds will be available shortly!</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Head offline warning */}
      {!headOnline && (
        <div className="w-full rounded-xl bg-sr-tangerine/10 p-3">
          <p className="text-xs text-sr-tangerine">
            Sugar Rush is offline right now. Please try again later.
          </p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="w-full rounded-xl bg-sr-salmon/10 p-3">
          <p className="text-xs text-sr-salmon">{error}</p>
        </div>
      )}
    </div>
  );
};
